// @flow
import * as React from 'react';
import type { ChildrenArray } from 'react';
import type { CompilerInjectedProps } from '../../validation/runtime-checks/index.js';
import Latex from '../../Latex.js';
import { LatexTextAtom } from '../../LatexTextAtom.js';
import { ContentContext } from '../../JsxContent.js';
import {
  types as t,
  usePropsValidation,
} from '../../validation/runtime-checks/index.js';
import { childrenArrayMatcher } from '../../childrenArrayMatcher.js';

export function RSLMultipleChoiceTextOptions(props: {|
  optionWidth: 'small' | 'medium' | 'large',
  children: ChildrenArray<React$Element<typeof RSLMultipleChoiceTextOption>>,
  ...CompilerInjectedProps,
|}) {
  const { enablePhoneLayout } = React.useContext(ContentContext);
  const { failedValidation } = usePropsValidation(
    props,
    RSLMultipleChoiceTextOptions,
  );
  if (failedValidation) return null;

  const { optionWidth, children } = props;
  const gridTemplateColumns = getGridTemplateColumns({
    optionWidth,
    enablePhoneLayout,
  });

  return (
    <div
      style={{
        display: 'grid',
        gap: 32,
        gridTemplateColumns,
        margin: '28px 0',
      }}
    >
      {React.Children.toArray(children).map((c, i) =>
        React.cloneElement(c, {
          _label: String.fromCharCode('A'.charCodeAt(0) + i),
        }),
      )}
    </div>
  );
}
RSLMultipleChoiceTextOptions.spec = {
  // This is the preferred width for each of the options.
  // We use this small/medium/large terminology (rather than
  // something like numCols={4}) as in the phone layout the number
  // of columns must be adjusted to fit the layout constraints.
  optionWidth: t.unionOfValues('small', 'medium', 'large'),
  children: childrenArrayMatcher({
    min: 2,
    max: 6,
    elementType: t.elementType(RSLMultipleChoiceTextOption),
  }),
};

function getGridTemplateColumns({ optionWidth, enablePhoneLayout }) {
  const oneCol = '1fr';
  const twoCol = '1fr 1fr';
  const fourCol = '1fr 1fr 1fr 1fr';

  switch (optionWidth) {
    case 'small':
      return enablePhoneLayout ? twoCol : fourCol;
    case 'medium':
      return enablePhoneLayout ? oneCol : twoCol;
    case 'large':
      return '1fr';
    default:
      (optionWidth: empty);
      throw Error('Exhaustive pattern matching violated at runtime');
  }
}

export function RSLMultipleChoiceTextOption(props: {|
  _label: string,
  children: ChildrenArray<
    string | React$Element<typeof Latex> | React$Element<typeof LatexTextAtom>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    RSLMultipleChoiceTextOption,
  );
  if (failedValidation) return null;

  return (
    <div style={{ display: 'flex' }}>
      <div style={{ width: 25, flexShrink: 0, fontWeight: 700 }}>
        {props._label}
      </div>
      <div>{props.children}</div>
    </div>
  );
}
RSLMultipleChoiceTextOption.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      t.string,
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
    ),
  ),
};
